// AI 客服管理控制台 (fuelio-chat /admin/* 的 UI)
//
// Layout: 2-column.
//   Left  = 工单列表 (默认 pending escalations) + 全部对话切换 tab.
//   Right = 选中对话详情：用户/AI 消息流 + 工具调用 / RAG 命中 / 备注
//
// 跟 ChatScreen 的区别：
//   - ChatScreen 是人工 ↔ 用户的实时 WS 对话（fuelio-api 的 ChatHubDO）
//   - AIChat 是 AI ↔ 用户的事后审计 / 接单（fuelio-chat 的 SSE + DB_CHAT）
//
// 没有实时 push — 用 15 秒轮询，简单稳定。需要实时再上 SSE long-poll。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircle2, RefreshCw, AlertCircle, MessageSquare, Inbox, Filter } from 'lucide-react'
import {
  chatApi,
  type ConversationRow, type EscalationRow, type ConversationDetail,
  ChatApiError,
  extractMessageText, extractMessageAuthor,
  parseToolCalls, parseRagChunks, formatTime,
} from '@/lib/chat-api'
import { useStaleCache } from '@/lib/cache'
import { api } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'

type Tab = 'escalations' | 'conversations'
type EscFilter = 'pending' | 'assigned' | 'resolved' | 'all'

const POLL_MS = 15_000

export function AIChatScreen() {
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('escalations')
  const [escFilter, setEscFilter] = useState<EscFilter>('pending')
  const [escalations, setEscalations] = useState<EscalationRow[]>([])
  const [conversations, setConversations] = useState<ConversationRow[]>([])
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null)
  const [detail, setDetail] = useState<ConversationDetail | null>(null)
  const [loadingList, setLoadingList] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyAction, setBusyAction] = useState<string | null>(null)

  // Admin email — used as assigned_to header for assign/note endpoints.
  const { data: who } = useStaleCache('admin.whoami.v1', () => api.whoami().then((r) => r.admin))
  const adminEmail = who?.email ?? 'unknown_admin'

  // Load list (escalations OR conversations).
  const loadList = useCallback(async () => {
    setLoadingList(true)
    setError(null)
    try {
      if (tab === 'escalations') {
        const status = escFilter === 'all' ? undefined : escFilter
        const r = await chatApi.listEscalations({ status, limit: 100 })
        setEscalations(r.escalations)
      } else {
        const r = await chatApi.listConversations({ limit: 100 })
        setConversations(r.conversations)
      }
    } catch (err) {
      const msg = err instanceof ChatApiError
        ? `HTTP ${err.status}: ${err.body.slice(0, 100)}`
        : err instanceof Error ? err.message : String(err)
      setError(msg)
    } finally {
      setLoadingList(false)
    }
  }, [tab, escFilter])

  // Load conversation detail when selection changes.
  const loadDetail = useCallback(async (id: string) => {
    setLoadingDetail(true)
    try {
      const d = await chatApi.getConversation(id)
      setDetail(d)
    } catch (err) {
      const msg = err instanceof ChatApiError
        ? `HTTP ${err.status}`
        : err instanceof Error ? err.message : String(err)
      toast.error('加载会话失败', msg)
    } finally {
      setLoadingDetail(false)
    }
  }, [toast])

  // Effect: refresh list on tab/filter change + every POLL_MS.
  useEffect(() => {
    void loadList()
    const id = window.setInterval(() => void loadList(), POLL_MS)
    return () => window.clearInterval(id)
  }, [loadList])

  // Effect: load detail when selectedConvId changes. No polling — /ai-chat
  // is for ticket audit, not live chat. Use the refresh button if needed.
  useEffect(() => {
    if (!selectedConvId) { setDetail(null); return }
    void loadDetail(selectedConvId)
  }, [selectedConvId, loadDetail])

  async function assign(esc: EscalationRow) {
    if (busyAction) return
    setBusyAction(esc.id)
    try {
      await chatApi.assignEscalation(esc.id, adminEmail)
      toast.success(`已接单 #${esc.id.slice(0, 10)}`)
      await loadList()
    } catch (err) {
      toast.error('接单失败', err instanceof Error ? err.message : String(err))
    } finally { setBusyAction(null) }
  }

  async function resolve(esc: EscalationRow) {
    if (busyAction) return
    setBusyAction(esc.id)
    try {
      await chatApi.resolveEscalation(esc.id)
      toast.success(`已标记解决 #${esc.id.slice(0, 10)}`)
      await loadList()
      if (selectedConvId === esc.conversation_id) await loadDetail(esc.conversation_id)
    } catch (err) {
      toast.error('操作失败', err instanceof Error ? err.message : String(err))
    } finally { setBusyAction(null) }
  }

  async function addNote() {
    if (!selectedConvId) return
    const text = window.prompt('备注内容（admin 视角的系统消息）')
    if (!text || !text.trim()) return
    try {
      await chatApi.addNote(selectedConvId, text.trim(), adminEmail)
      toast.success('备注已添加')
      await loadDetail(selectedConvId)
    } catch (err) {
      toast.error('添加失败', err instanceof Error ? err.message : String(err))
    }
  }

  // sendReply moved to /chat. Live human chat lives in fuelio-api's
  // ChatHubDO; /ai-chat is for audit + ticket actions only.

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-2 md:flex-row md:gap-3">
      {/* ───── Left column: list ───── */}
      <div className="flex w-full flex-col rounded-md border bg-card md:w-[360px]">
        {/* Tabs */}
        <div className="flex border-b">
          <TabButton active={tab === 'escalations'} onClick={() => { setTab('escalations'); setSelectedConvId(null) }}>
            <Inbox className="h-4 w-4"/>工单 ({escalations.length})
          </TabButton>
          <TabButton active={tab === 'conversations'} onClick={() => { setTab('conversations'); setSelectedConvId(null) }}>
            <MessageSquare className="h-4 w-4"/>全部对话
          </TabButton>
        </div>

        {/* Filter row */}
        {tab === 'escalations' && (
          <div className="flex items-center gap-2 border-b px-3 py-2 text-xs">
            <Filter className="h-3.5 w-3.5 text-muted-foreground"/>
            {(['pending', 'assigned', 'resolved', 'all'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setEscFilter(s)}
                className={cn(
                  'rounded px-2 py-1 transition',
                  escFilter === s
                    ? 'bg-primary/15 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {ESC_FILTER_LABEL[s]}
              </button>
            ))}
            <button onClick={() => void loadList()} className="ml-auto rounded p-1 hover:bg-muted" title="刷新">
              <RefreshCw className={cn('h-3.5 w-3.5', loadingList && 'animate-spin')}/>
            </button>
          </div>
        )}

        {/* List body */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="m-3 flex items-start gap-2 rounded border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"/>
              <span>{error}</span>
            </div>
          )}
          {tab === 'escalations'
            ? escalations.length === 0
              ? <EmptyHint text={loadingList ? '加载中…' : '没有工单'}/>
              : escalations.map((esc) => (
                  <EscalationItem
                    key={esc.id}
                    esc={esc}
                    selected={selectedConvId === esc.conversation_id}
                    busy={busyAction === esc.id}
                    onSelect={() => setSelectedConvId(esc.conversation_id)}
                    onAssign={() => assign(esc)}
                    onResolve={() => resolve(esc)}
                  />
                ))
            : conversations.length === 0
              ? <EmptyHint text={loadingList ? '加载中…' : '没有对话'}/>
              : conversations.map((c) => (
                  <ConversationItem
                    key={c.id}
                    conv={c}
                    selected={selectedConvId === c.id}
                    onSelect={() => setSelectedConvId(c.id)}
                  />
                ))}
        </div>
      </div>

      {/* ───── Right column: detail ───── */}
      <div className="flex flex-1 flex-col rounded-md border bg-card overflow-hidden">
        {!selectedConvId ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            从左侧选择一个会话查看详情
          </div>
        ) : !detail || loadingDetail ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin"/> 加载中…
          </div>
        ) : (
          <ConversationDetailView
            detail={detail}
            onAddNote={addNote}
            onResolveAll={async () => {
              for (const esc of detail.escalations.filter((e) => e.status !== 'resolved')) {
                await resolve(esc)
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

// ─── List items ────────────────────────────────────────────────────────────

function EscalationItem({
  esc, selected, busy, onSelect, onAssign, onResolve,
}: {
  esc: EscalationRow; selected: boolean; busy: boolean
  onSelect: () => void; onAssign: () => void; onResolve: () => void
}) {
  return (
    <div
      onClick={onSelect}
      className={cn(
        'cursor-pointer border-b px-3 py-2.5 transition',
        selected ? 'bg-primary/10' : 'hover:bg-muted/50',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs">
            <StatusPill status={esc.status}/>
            <span className="text-muted-foreground">{formatTime(esc.created_at)}</span>
          </div>
          <div className="mt-1 line-clamp-2 text-sm leading-snug">
            {esc.summary ?? esc.reason ?? <em className="text-muted-foreground">（无摘要）</em>}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>类型: {REASON_LABEL[esc.reason_type ?? 'other'] ?? esc.reason_type ?? '-'}</span>
            {esc.assigned_to && <span>· {esc.assigned_to}</span>}
          </div>
        </div>
      </div>
      {/* Action row appears for pending tickets only. */}
      {(esc.status === 'pending' || esc.status === 'assigned') && (
        <div className="mt-2 flex items-center gap-2">
          {esc.status === 'pending' && (
            <button
              onClick={(e) => { e.stopPropagation(); onAssign() }}
              disabled={busy}
              className="rounded bg-primary px-2 py-1 text-[11px] text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {busy ? '处理中…' : '接单'}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onResolve() }}
            disabled={busy}
            className="rounded border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50"
          >
            <CheckCircle2 className="mr-1 inline h-3 w-3"/>标记解决
          </button>
        </div>
      )}
    </div>
  )
}

function ConversationItem({
  conv, selected, onSelect,
}: {
  conv: ConversationRow; selected: boolean; onSelect: () => void
}) {
  return (
    <div
      onClick={onSelect}
      className={cn(
        'cursor-pointer border-b px-3 py-2.5 transition',
        selected ? 'bg-primary/10' : 'hover:bg-muted/50',
      )}
    >
      <div className="flex items-center justify-between text-xs">
        <span className="truncate font-mono text-[11px]">{conv.id.slice(0, 16)}</span>
        <span className="text-muted-foreground">{formatTime(conv.last_active_at)}</span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <StatusPill status={conv.status}/>
        <span>· {conv.msg_count} 条消息</span>
        {conv.user_id && <span>· {conv.user_id.slice(0, 10)}…</span>}
      </div>
    </div>
  )
}

// ─── Detail view ───────────────────────────────────────────────────────────

function ConversationDetailView({
  detail, onAddNote, onResolveAll,
}: {
  detail: ConversationDetail
  onAddNote: () => void
  onResolveAll: () => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [detail])

  const hasOpenTicket = detail.escalations.some((e) => e.status !== 'resolved')

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusPill status={detail.conversation.status}/>
            <span className="truncate font-mono text-xs text-muted-foreground">{detail.conversation.id}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            用户 {detail.conversation.user_id ?? '游客'} · 起始 {formatTime(detail.conversation.started_at)} · {detail.messages.length} 条消息
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onAddNote} className="rounded border px-2 py-1 text-xs hover:bg-muted">
            + 添加备注
          </button>
          {hasOpenTicket && (
            <button onClick={onResolveAll} className="rounded border px-2 py-1 text-xs hover:bg-muted">
              <CheckCircle2 className="mr-1 inline h-3 w-3"/>关闭工单
            </button>
          )}
        </div>
      </div>

      {/* Escalation banner */}
      {detail.escalations.length > 0 && (
        <div className="border-b bg-amber-50 px-4 py-2 text-xs dark:bg-amber-950/30">
          {detail.escalations.map((esc) => (
            <div key={esc.id} className="flex items-center gap-2">
              <StatusPill status={esc.status}/>
              <span>工单 #{esc.id.slice(0, 12)}</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">{REASON_LABEL[esc.reason_type ?? 'other'] ?? esc.reason_type}</span>
              {esc.assigned_to && <>
                <span className="text-muted-foreground">·</span>
                <span>{esc.assigned_to}</span>
              </>}
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {detail.messages.map((m) => <MessageBlock key={m.id} m={m}/>)}
      </div>

      {/* Footer hint: live human chat lives in /chat (fuelio-api ChatHubDO).
       *  /ai-chat is for ticket audit + actions; this prevents admins from
       *  expecting to be able to reply here. */}
      <div className="border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
        💡 想直接回复用户？请到 <a href="/chat" className="font-medium text-primary hover:underline">在线客服</a> 进行实时聊天。
      </div>
    </>
  )
}

function MessageBlock({ m }: { m: { id: string; role: string; content: string; tool_calls: string | null; rag_chunks: string | null; latency_ms: number | null; input_tokens: number | null; output_tokens: number | null; created_at: number } }) {
  const text = extractMessageText(m.content)
  const author = extractMessageAuthor(m.content)
  const tools = parseToolCalls(m.tool_calls)
  const rag = parseRagChunks(m.rag_chunks)
  const isUser = m.role === 'user'
  const isSystem = m.role === 'system'
  const isAdmin = m.role === 'admin'

  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <RoleBadge role={m.role}/>
        {/* For admin/system rows the author email is the most useful piece of
         *  context; show inline next to the role badge so admin can scan
         *  "who said what" at a glance. */}
        {author && (isAdmin || isSystem) && <span className="text-muted-foreground">{author}</span>}
        <span>{formatTime(m.created_at)}</span>
        {m.latency_ms != null && <span>· {m.latency_ms}ms</span>}
        {(m.input_tokens != null || m.output_tokens != null) && (
          <span>· {(m.input_tokens ?? 0) + (m.output_tokens ?? 0)} tok</span>
        )}
      </div>
      <div className={cn(
        'rounded-md px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
        isUser
          ? 'bg-primary/10'
          : isSystem
            ? 'bg-amber-100/60 italic dark:bg-amber-950/30'
            : isAdmin
              ? 'bg-emerald-100/60 dark:bg-emerald-950/30'
              : 'bg-muted',
      )}>
        {text || <em className="text-muted-foreground">（空消息）</em>}
      </div>
      {/* Tool calls under the message */}
      {tools.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {tools.map((tc) => (
            <details key={tc.id} className="rounded border bg-blue-50/50 px-2 py-1 text-[11px] dark:bg-blue-950/20">
              <summary className="cursor-pointer text-blue-700 dark:text-blue-300">
                🔧 {tc.name} <span className="font-mono opacity-60">({tc.id.slice(0, 10)})</span>
              </summary>
              <div className="mt-1 space-y-1 font-mono">
                <div><span className="text-muted-foreground">input:</span> <code className="text-[10px]">{JSON.stringify(tc.input)}</code></div>
                <div><span className="text-muted-foreground">output:</span> <code className="text-[10px]">{JSON.stringify(tc.output).slice(0, 400)}</code></div>
              </div>
            </details>
          ))}
        </div>
      )}
      {/* RAG hits — collapsed by default. */}
      {rag.length > 0 && (
        <details className="mt-1.5 rounded border bg-emerald-50/50 px-2 py-1 text-[11px] dark:bg-emerald-950/20">
          <summary className="cursor-pointer text-emerald-700 dark:text-emerald-300">
            📚 RAG 命中 {rag.length} 条
          </summary>
          <ul className="mt-1 space-y-1">
            {rag.map((c, i) => (
              <li key={i} className="font-mono">
                <span className="text-muted-foreground">[{c.score.toFixed(2)}]</span> {c.source}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

// ─── Bits & pieces ─────────────────────────────────────────────────────────

const ESC_FILTER_LABEL = {
  pending: '待处理',
  assigned: '已接单',
  resolved: '已解决',
  all: '全部',
} as const

const REASON_LABEL: Record<string, string> = {
  user_request: '用户主动',
  account_banned: '账号被封',
  refund_dispute: '退款纠纷',
  image_upload: '上传图片',
  unresolved: '未解决',
  other: '其他',
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    active:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    escalated:  'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    closed:     'bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300',
    pending:    'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
    assigned:   'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    resolved:   'bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-400',
  }
  const label: Record<string, string> = {
    active: 'AI 接管', escalated: '已转人工', closed: '已关闭',
    pending: '待处理', assigned: '处理中', resolved: '已解决',
  }
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium', map[status] ?? '')}>
      {label[status] ?? status}
    </span>
  )
}

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    user:      { label: '用户',     cls: 'bg-primary/15 text-primary' },
    assistant: { label: 'AI 助手',  cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
    admin:     { label: '人工客服', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
    system:    { label: '内部备注', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' },
    tool:      { label: '工具',     cls: 'bg-purple-100 text-purple-700' },
  }
  const v = map[role] ?? { label: role, cls: 'bg-muted text-muted-foreground' }
  return <span className={cn('rounded px-1.5 py-0.5 text-[10px]', v.cls)}>{v.label}</span>
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2 text-sm transition',
        active
          ? 'border-primary font-medium text-primary'
          : 'border-transparent text-muted-foreground hover:bg-muted/50',
      )}
    >
      {children}
    </button>
  )
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
      {text}
    </div>
  )
}
